import React from "react";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  JobsTimesheetsFilterFields,
  buildJobsTimesheetsFilterChips,
} from "../filters";
import { AtlasListToolbar } from "../list-toolbar";
import TimesheetsPrintableContent from "./TimesheetsPrintableContent";
import useTimesheetsPanel from "./useTimesheetsPanel";
import { getTodayDateString, triggerTimesheetPrint } from "../../utils/timesheetViewHelpers";
import {
  formatAppIntlLabel,
  addAppCalendarDays,
  parseDbDateTime,
  getAppTodayIso,
} from "../../utils/appDateTime";
import "../../Styles/PrintTimesheet.css";

const primaryPrintButtonClass =
  "bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600";

export default function TimesheetsPanel({
  variant = "admin",
  active = true,
  staffRequestId = null,
  selectedRequest = null,
  editableRequest = null,
  supervisors = [],
  className = "",
  contentOffsetClass = "",
}) {
  const filterPanel = useFilterPanel();
  const panel = useTimesheetsPanel({
    variant,
    active,
    staffRequestId,
  });

  const {
    filterClient,
    setFilterClient,
    clientSearch,
    setClientSearch,
    filterClientList,
    filterLocationList,
    filterLocations,
    setFilterLocations,
    filterMode,
    setFilterMode,
    filterStartDate,
    setFilterStartDate,
    filterRange,
    setFilterRange,
    searchTerm,
    setSearchTerm,
    data,
    loading,
    clientLoading,
    timesheetPrintOrientation,
    setTimesheetPrintOrientation,
    showTimesheetPrintDropdown,
    setShowTimesheetPrintDropdown,
    printWithoutValues,
    setPrintWithoutValues,
    timesheetPrintDropdownRef,
    refresh,
    clearAllFilters,
    removeFilterChip,
  } = panel;

  const chips = buildJobsTimesheetsFilterChips({
    clientSearch,
    filterLocations,
    filterLocationList,
    filterMode,
    filterStartDate,
    filterRange,
    printWithoutValues,
    today: getTodayDateString(),
  });

  const getPrintTitle = React.useCallback(() => {
    const titleParts = ["Sign in"];
    if (clientSearch) {
      titleParts.push(clientSearch);
    }
    const baseDate = filterStartDate || getAppTodayIso();
    const rangeDays = filterRange
      ? parseInt(filterRange, 10)
      : filterMode === "week"
        ? 7
        : 7;
    const endDateIso = addAppCalendarDays(baseDate, Math.max(rangeDays, 1) - 1);
    const startAnchor = parseDbDateTime(`${baseDate}T12:00:00Z`);
    const endAnchor = parseDbDateTime(`${endDateIso}T12:00:00Z`);

    const startDateLabel = formatAppIntlLabel(startAnchor, { month: "short", day: "numeric" });
    const endDateLabel = formatAppIntlLabel(endAnchor, { month: "short", day: "numeric" });

    if (startDateLabel) {
      if (endDateLabel && baseDate !== endDateIso) {
        titleParts.push(`${startDateLabel} to ${endDateLabel}`);
      } else {
        titleParts.push(startDateLabel);
      }
    }
    return titleParts.join(" - ");
  }, [clientSearch, filterStartDate, filterRange, filterMode]);

  const handlePrint = React.useCallback(() => {
    triggerTimesheetPrint({
      isWeekMode: filterMode === "week",
      orientation: filterMode === "week" ? "landscape" : timesheetPrintOrientation,
      documentTitle: getPrintTitle(),
    });
  }, [filterMode, getPrintTitle, timesheetPrintOrientation]);

  const printActions =
    filterMode === "day" ? (
      <div className="relative inline-block" ref={timesheetPrintDropdownRef}>
        <div className="flex overflow-hidden rounded-lg border border-red-600">
          <button
            type="button"
            onClick={handlePrint}
            className={primaryPrintButtonClass}
          >
            PRINT
          </button>
          <button
            type="button"
            onClick={() => setShowTimesheetPrintDropdown((v) => !v)}
            className="flex items-center border-l border-red-700 bg-sidebar-active px-2 py-2 text-white hover:bg-red-600"
            aria-label="Print orientation"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        {showTimesheetPrintDropdown && (
          <>
            <div
              className="fixed inset-0 z-[99]"
              onClick={() => setShowTimesheetPrintDropdown(false)}
              aria-hidden="true"
            />
            <div className="absolute right-0 top-full z-[100] mt-1 min-w-[120px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              {["portrait", "landscape"].map((orientation) => (
                <button
                  key={orientation}
                  type="button"
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                    timesheetPrintOrientation === orientation
                      ? "bg-red-50 font-medium text-red-700"
                      : "text-gray-700"
                  }`}
                  onClick={() => {
                    setTimesheetPrintOrientation(orientation);
                    setShowTimesheetPrintDropdown(false);
                    triggerTimesheetPrint({
                      orientation,
                      documentTitle: getPrintTitle(),
                    });
                  }}
                >
                  {orientation === "portrait" ? "Portrait" : "Landscape"}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    ) : (
      <button
        type="button"
        onClick={handlePrint}
        className={`rounded-lg ${primaryPrintButtonClass}`}
      >
        PRINT
      </button>
    );

  return (
    <div className={`flex w-full min-w-0 max-w-full flex-col flex-1 min-h-0 ${className}`}>
      <fieldset className="border-0 m-0 p-0 min-w-0 flex flex-col flex-1 min-h-0">
        <div className={`min-w-0 max-w-full ${contentOffsetClass || filterPanel.contentOffsetClass}`}>
          <div className="timesheet-print-hide">
            <AtlasListToolbar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search timesheets"
              onFilterClick={filterPanel.toggle}
              filterOpen={filterPanel.isOpen}
              onRefresh={refresh}
              resultsMeta={null}
              actions={printActions}
              chipBar={
                <AtlasFilterChipBar
                  chips={chips}
                  onChipClick={(chip) => {
                    if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                  }}
                  onRemove={removeFilterChip}
                  onClearAll={clearAllFilters}
                />
              }
            />
          </div>

          <TimesheetsPrintableContent
            data={data}
            loading={loading}
            filterClient={filterClient}
            clientSearch={clientSearch}
            filterClientList={filterClientList}
            filterLocations={filterLocations}
            filterLocationList={filterLocationList}
            filterMode={filterMode}
            filterStartDate={filterStartDate}
            filterRange={filterRange}
            searchTerm={searchTerm}
            timesheetPrintOrientation={timesheetPrintOrientation}
            printWithoutValues={printWithoutValues}
            selectedRequest={selectedRequest}
            editableRequest={editableRequest}
            supervisors={supervisors}
          />

          <div className="timesheet-print-hide">
            <AtlasFilterPanel
              isOpen={filterPanel.isOpen}
              onClose={filterPanel.close}
              onClear={clearAllFilters}
              activeCount={chips.length}
              title="Timesheets Filters"
            >
              <JobsTimesheetsFilterFields
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocus}
                filterClient={filterClient}
                clientSearch={clientSearch}
                filterClientList={filterClientList}
                filterLocationList={filterLocationList}
                filterLocations={filterLocations}
                filterMode={filterMode}
                filterStartDate={filterStartDate}
                filterRange={filterRange}
                clientLoading={clientLoading}
                printWithoutValues={printWithoutValues}
                setFilterClient={setFilterClient}
                setClientSearch={setClientSearch}
                setFilterLocation={() => {}}
                setFilterLocationList={() => {}}
                setFilterLocations={setFilterLocations}
                setFilterMode={setFilterMode}
                setFilterStartDate={setFilterStartDate}
                setFilterRange={setFilterRange}
                setPrintWithoutValues={setPrintWithoutValues}
              />
            </AtlasFilterPanel>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
